import type { FC } from 'react';
import { useStoreState } from 'easy-peasy';
import { candidateSelector } from '@model/Candidate/Candidate.selectors';
import { Icon, ICON_NAMES } from '@reverse-hr/design-system';
import { ProfileInfoListItem } from '@components/Molecules/ProfileInfoList/_partials/ProfileInfoListItem';
import { selectorUser } from '@model/User';
import { utilityGetTranslatedLocation } from '@utils/location';
import { getDrivingTimeToWorkContent } from '@utils/candidate-location-info';
import { Trans, useTranslation } from 'react-i18next';

export const ProfileInfoList: FC = () => {
  const { t } = useTranslation('', {
    keyPrefix: 'candidateDetails.info',
  });

  const { t: tShowcase } = useTranslation('', {
    keyPrefix: 'showcase',
  });

  const candidate = useStoreState(candidateSelector);
  const { locale } = useStoreState(selectorUser);

  if (!candidate) {
    return null;
  }

  const candidateAge = candidate.yearOfBirth
    ? new Date().getFullYear() - candidate.yearOfBirth
    : null;

  const candidateAddress = utilityGetTranslatedLocation({
    location: candidate.address,
    language: locale.language,
  });

  const drivingTimeToWorkContent = getDrivingTimeToWorkContent(candidate);

  const drivingDistanceToWork = candidate.drivingDistanceToWorkInKilometers
    ? Math.round(candidate.drivingDistanceToWorkInKilometers)
    : null;

  return (
    <ul className="c-profile-info-list">
      <ProfileInfoListItem icon={ICON_NAMES.PHONE_OUTLINE}>
        {candidate.phoneNumber}
      </ProfileInfoListItem>

      <ProfileInfoListItem icon={ICON_NAMES.MAIL}>
        {candidate.email}
      </ProfileInfoListItem>

      <ProfileInfoListItem icon={ICON_NAMES.LINKEDIN}>
        {candidate.linkedinUrl}
      </ProfileInfoListItem>

      <ProfileInfoListItem
        className="c-profile-info-list__item--age"
        icon={ICON_NAMES.GROUP_ALT}
      >
        {candidateAge ? t('age', { age: candidateAge }) : null}
      </ProfileInfoListItem>

      <ProfileInfoListItem icon={ICON_NAMES.BUILDING_COMPANY}>
        {candidate.currentCompanyName}
      </ProfileInfoListItem>

      {candidateAddress && (
        <ProfileInfoListItem icon={ICON_NAMES.MAP}>
          <p className="c-profile-info-list__location">
            <Trans
              context={drivingDistanceToWork ? 'withDistance' : ''}
              i18nKey="location.locationAndDistance"
              t={tShowcase}
              values={{
                location: candidateAddress.city,
                distance: drivingDistanceToWork,
              }}
            />
          </p>

          <Icon name={ICON_NAMES.SMALL_LONG_RIGHT} />

          <p className="c-profile-info-list__driving-time">
            <Icon name={ICON_NAMES.CLOCK} />
            <span>{drivingTimeToWorkContent}</span>
          </p>
        </ProfileInfoListItem>
      )}
    </ul>
  );
};
